import { FormEvent, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import styles from "./Pagamento.module.css";
import Header from "../../components/Header/Header";
import SubHeader from "../../components/SubHeader/SubHeader";
import { THEME } from "../../utils/THEME";
import { MdKeyboardArrowRight } from "react-icons/md";
import Select from "../../components/Select/Select";
import { FaPix } from "react-icons/fa6";
import { FaCreditCard } from "react-icons/fa";
import api from "../../utils/axios/api";
import { createCardToken, getIdentificationTypes, getPaymentMethods } from "@mercadopago/sdk-react";
import MpCardSecureFields from "../../components/MpCardSecureFields/MpCardSecureFields";
import MpSavedCardSecurityCode from "../../components/MpSavedCardSecurityCode/MpSavedCardSecurityCode";
import DropdownGray from "../../components/DropdownGray/DropdownGray";
import { useLocation, useNavigate } from "react-router-dom";
import {
  DEFAULT_ESTIMATED_DELIVERY_DAYS,
  formatEstimatedDeliveryDate,
  localeFromAppLanguage,
} from "../../utils/estimatedDeliveryDate";
import { getDefaultDeliveryFreightAmount } from "../../utils/defaultDeliveryFreight";

type Address = {
  city: string,
  complement: string,
  district: string,
  id: string,
  name: string,
  number: string,
  state: string,
  street: string,
  zipCode: string,
}

type Store = {
  id: string,
  name: string,
  serviceDelivery: string,
  servicePickup: string
}

type Card = {
  id: string;
  idMercadoPago: string;
  flag: string;
  lastFourDigits: string;
  name: string;
}

export default function Pagamento() {
  const { t, i18n } = useTranslation();

  const deliveryReceiveByLabel = useMemo(
    () =>
      t("pagamento.recebaAte", {
        date: formatEstimatedDeliveryDate({ language: i18n.language }),
      }),
    [t, i18n.language],
  );

  const defaultFreightFormatted = useMemo(
    () =>
      new Intl.NumberFormat(localeFromAppLanguage(i18n.language), {
        style: "currency",
        currency: "BRL",
      }).format(getDefaultDeliveryFreightAmount()),
    [i18n.language],
  );

  const [address, setAddress] = useState<Address[]>([]);
  const [Selectedaddress, setSelectedAddress] = useState<string>("");
  const [parcelas, setParcelas] = useState<string>("1");
  const clientId = localStorage.getItem('clientId');
  const [optionSelected, setOptionSelected] = useState<"pix" | "credit_card" | "new_credit_card" | null>(null)
  const [error, setError] = useState('')
  const [optionDeliverySelected, setOptionDeliverySelected] = useState('')
  const [store, setStore] = useState<Store | null>(null)
  const [freight, setFreight] = useState<number | null>(null)
  const [dateDelivery, setDateDelivery] = useState<Date | null>(null)
  const [cards, setCards] = useState<Card[]>([])
  const [cardSelected, setCardSelected] = useState<string | null>(null)
  const location = useLocation()
  const navigate = useNavigate()
  const [isMobileMenuVisible, setIsMobileMenuVisible] = useState<boolean>(true);
  const [isSmallScreen, setIsSmallScreen] = useState<boolean>(
    window.innerWidth < 768
  );

  const [identificationTypes, setIdentificationTypes] = useState<
    { id: string; name: string; min_length: number; max_length: number }[]
  >([]);
  const [identificationType, setIdentificationType] = useState<string>("");
  const [identificationNumber, setIdentificationNumber] = useState("");
  const secureFieldsValidRef = useRef({
    cardNumber: false,
    securityCode: false,
    expirationDate: false,
  });
  const savedCardSecurityCodeValidRef = useRef(false);
  const [isSavingCard, setIsSavingCard] = useState(false);
  const [isCreatingSavedCardToken, setIsCreatingSavedCardToken] = useState(false);

  const onMpFieldValidity = useCallback(
    (field: "cardNumber" | "securityCode" | "expirationDate", errorMessages: Array<{ message: string }>) => {
      secureFieldsValidRef.current[field] = errorMessages.length === 0;
    },
    []
  );

  const onSavedCardSecurityCodeValidity = useCallback((isValid: boolean) => {
    savedCardSecurityCodeValidRef.current = isValid;
  }, []);

  useEffect(() => {
    (async () => {
      const types = await getIdentificationTypes();
      if (types?.length) {
        setIdentificationTypes(types);
        const cpf = types.find((t) => t.id === "CPF");
        setIdentificationType(cpf?.id ?? types[0].id);
      }
    })();
  }, []);

  function handleWindowSizeChange() {
    setIsSmallScreen(window.innerWidth < 768);
  }

  useEffect(() => {
    window.addEventListener('resize', handleWindowSizeChange);
    // Executar uma vez no início para garantir que o estado é atualizado com a largura da janela atual
    handleWindowSizeChange();

    return () => window.removeEventListener('resize', handleWindowSizeChange);
  }, []);

  const { carts, voucher }: { carts: string[], voucher?: { name: string; discount: number; valid: boolean } } = location.state || {}

  useEffect(() => {
    const fetchClientAddress = async () => {
      try {
        const [response, responseStore, responseCards] = await Promise.all([
          api.get<{ address: Address }[]>(`/address/client/${clientId}`),
          api.get(`/cart/${carts[0]}`),
          api.get(`/card/clientId/${clientId}`)
        ]);

        setAddress(response.data.map(item => item.address));
        const { id, name, serviceDelivery, servicePickup } = responseStore.data.product.store
        setStore({ id, name, serviceDelivery, servicePickup })
        setCards(responseCards.data.map((item: Card) => item))
      } catch (error) {
        console.error("Error fetching client address:", error);
      }
    };

    fetchClientAddress();
  }, []);

  const getSelectedAddressDetails = () => {
    return address.find((item: any) => item.name === Selectedaddress);
  };

  const selectedAddressDetails = getSelectedAddressDetails();

  const SubForm = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (!selectedAddressDetails) {
      setError('Endereço não selecionado')
      return
    }

    if (!optionDeliverySelected) {
      setError('Opção de entrega não selecionada')
      return
    }

    if (!optionSelected) {
      setError('Opção de pagamento não selecionada')
      return
    }

    if (optionSelected === 'credit_card' && !cardSelected) {
      setError('Escolha ou adicione um cartão')
      return
    }

    setError('')
    let savedCardToken = "";
    let directPaymentMethodId = "";
    let cardTokenParams: {
      cardholderName?: string;
      identificationType?: string;
      identificationNumber?: string;
      cardId?: string;
    } | null = null;

    try {
      if (optionSelected === "new_credit_card") {
        if (!cardName.trim()) {
          setError("Informe o nome no cartão");
          return;
        }
        if (!identificationTypes.length) {
          setError("Aguarde o carregamento dos tipos de documento.");
          return;
        }
        if (!identificationType || !identificationNumber.trim()) {
          setError("Informe o tipo e o número do documento do titular");
          return;
        }

        const idDigits = identificationNumber.replace(/\D/g, "");
        const idRule = identificationTypes.find((t) => t.id === identificationType);
        if (idRule && (idDigits.length < idRule.min_length || idDigits.length > idRule.max_length)) {
          setError("Número de documento inválido para o tipo selecionado");
          return;
        }

        const s = secureFieldsValidRef.current;
        if (!s.cardNumber || !s.securityCode || !s.expirationDate) {
          setError("Preencha número, vencimento e CVV do cartão corretamente");
          return;
        }

        cardTokenParams = {
          cardholderName: cardName.trim(),
          identificationType,
          identificationNumber: idDigits,
        };
      }

      if (optionSelected === "credit_card") {
        if (!savedCardSecurityCodeValidRef.current) {
          setError("Informe o CVV do cartão selecionado");
          return;
        }

        const selectedCard = cards.find((card) => card.id === cardSelected);
        if (!selectedCard?.idMercadoPago) {
          setError("Cartão selecionado inválido");
          return;
        }

        directPaymentMethodId = selectedCard.flag;
        if (!directPaymentMethodId) {
          setError("Bandeira do cartão selecionado inválida");
          return;
        }

        cardTokenParams = {
          cardId: selectedCard.idMercadoPago,
        };
      }

      if (cardTokenParams) {
        setIsCreatingSavedCardToken(true);
        const token = await createCardToken(cardTokenParams);

        if (!token?.id) {
          setError(
            optionSelected === "credit_card"
              ? "Não foi possível gerar o token do cartão selecionado"
              : "Não foi possível gerar o token do cartão"
          );
          return;
        }

        if (optionSelected === "new_credit_card") {
          const methods = token.first_six_digits
            ? await getPaymentMethods({ bin: token.first_six_digits })
            : undefined;

          directPaymentMethodId = methods?.results?.[0]?.id ?? "";
          if (!directPaymentMethodId) {
            setError("Não foi possível identificar a bandeira do cartão");
            return;
          }
        }

        savedCardToken = token.id;
      }

      navigate("/ConfirmOrder", {
        state: {
          address: selectedAddressDetails,
          delivery: optionDeliverySelected,
          paymentSelected: optionSelected === "new_credit_card" ? "credit_card" : optionSelected,
          cardId: optionSelected === "new_credit_card" ? "" : cardSelected,
          cardToken: savedCardToken,
          paymentMethodId: directPaymentMethodId,
          freight,
          dateDelivery,
          installments: parcelas ? parseInt(parcelas) : 1,
          voucher: voucher ?? { name: "", discount: 0, valid: false },
        }
      });
    } catch (error) {
      console.error('Erro ao criar token do cartão:', error);
      setError("Erro ao gerar o token do cartão. Tente novamente.");
    } finally {
      setIsCreatingSavedCardToken(false);
    }
  };

  const [cardName, setCardName] = useState("");
  const [cardSurname, setCardSurname] = useState("");

  async function handleNewCard() {
    if (optionSelected !== "new_credit_card") {
      setOptionSelected("new_credit_card");
      setCardSelected(null);
      secureFieldsValidRef.current = {
        cardNumber: false,
        securityCode: false,
        expirationDate: false,
      };
      return;
    }

    setError("Salvar cartão está desativado temporariamente. Use Confirmar compra para pagar sem salvar.");
    return;

    /*
    // Fluxo de salvar cartão desativado temporariamente.
    if (!cardSurname.trim() || !cardName.trim()) {
      setError("Informe o apelido e o nome no cartão");
      return;
    }
    if (!identificationTypes.length) {
      setError("Aguarde o carregamento dos tipos de documento.");
      return;
    }
    if (!identificationType || !identificationNumber.trim()) {
      setError("Informe o tipo e o número do documento do titular");
      return;
    }

    const idDigits = identificationNumber.replace(/\D/g, "");
    const idRule = identificationTypes.find((t) => t.id === identificationType);
    if (idRule && (idDigits.length < idRule.min_length || idDigits.length > idRule.max_length)) {
      setError("Número de documento inválido para o tipo selecionado");
      return;
    }

    const s = secureFieldsValidRef.current;
    if (!s.cardNumber || !s.securityCode || !s.expirationDate) {
      setError("Preencha número, vencimento e CVV do cartão corretamente");
      return;
    }

    setError("");
    setIsSavingCard(true);
    try {
      const responseMp = await createCardToken({
        cardholderName: cardName.trim(),
        identificationType,
        identificationNumber: idDigits,
      });

      if (!responseMp?.id) {
        setError("Não foi possível tokenizar o cartão. Tente novamente.");
        return;
      }

      const response = await api.post("/card", {
        cardToken: responseMp.id,
        surnameCard: cardSurname.trim(),
        lastFourDigits: responseMp.last_four_digits,
      });
      setOptionSelected("credit_card");
      setCardSelected(response.data.id);
      setCards((prev) => [...prev, response.data]);
    } catch {
      setError("Erro ao cadastrar o cartão. Verifique os dados e tente novamente.");
    } finally {
      setIsSavingCard(false);
    }
    */
  }

  function handleSelectCard(cardId: string) {
    setCardSelected(cardId)
    savedCardSecurityCodeValidRef.current = false;
  }

  return (
    <div>
      <Header
        boxShadow
        mobileMenuActiveState={isMobileMenuVisible}
        toggleMobileMenu={setIsMobileMenuVisible}
        isSmallScreen={isSmallScreen}
        hamburgerIcon={false}
      />
      <div className={styles.SubHeader}>
        <SubHeader />
      </div>
      <div className={styles.body}>
        <div className={styles.pageInfo}>
          <p>Página Inicial</p>
          <MdKeyboardArrowRight size={22} color={THEME.COLORS.PRIMARY} />
          <p>Carrinho</p>
          <MdKeyboardArrowRight size={22} color={THEME.COLORS.PRIMARY} />
          <p>Entrega e pagamento</p>
        </div>

        <h1>Entrega e pagamento</h1>

        {store && <form className={styles.containerForm} onSubmit={SubForm}>
          <h2>Local de entrega</h2>

          <div className={styles.containerSelectAdress}>
            <h3>{store.name}</h3>
            <DropdownGray
              defaultValue="Escolha um endereço"
              selectedValue={Selectedaddress}
              setSelectedValue={setSelectedAddress}
              selectedValues={address.map(item => item.name)}
            />
            <div className={styles.wrapperAdress}>
              <input type="radio" name="address" id="entrega" onChange={() => {
                setOptionDeliverySelected('DELIVERY')
                setFreight(getDefaultDeliveryFreightAmount())
                const today = new Date();
                today.setDate(today.getDate() + DEFAULT_ESTIMATED_DELIVERY_DAYS)
                setDateDelivery(today)
              }} />
              <div>
                <label htmlFor="entrega">{deliveryReceiveByLabel}</label>
                <span>{defaultFreightFormatted}</span>
                <p>Endereço de entrega: {selectedAddressDetails ? `${selectedAddressDetails.street}, ${selectedAddressDetails.number}, ${selectedAddressDetails.district}, ${selectedAddressDetails.city}, ${selectedAddressDetails.state}, ${selectedAddressDetails.zipCode} - ${selectedAddressDetails.name}` : "Selecione um endereço"}</p>
              </div>
            </div>

            {store.servicePickup && <div className={styles.wrapperAdress}>
              <input type="radio" name="address" id="retirada" onChange={() => {
                setOptionDeliverySelected('PICKUP')
                setFreight(0)
                const today = new Date();
                today.setDate(today.getDate() + 3)
                setDateDelivery(today)
              }} />
              <div>
                <label htmlFor="retirada">Retire na loja a partir de 3 dias úteis após a confirmação da compra</label>
                <p>Endereço de entrega: {selectedAddressDetails ? `${selectedAddressDetails.street}, ${selectedAddressDetails.number}, ${selectedAddressDetails.district}, ${selectedAddressDetails.city}, ${selectedAddressDetails.state}, ${selectedAddressDetails.zipCode} - ${selectedAddressDetails.name}` : "Selecione um endereço"}</p>
              </div>
            </div>}
          </div>

          <h2>Forma de pagamento</h2>

          <div className={styles.containerSelectPayment}>
            <div className={styles.wrapperPayment}>
              <input type="radio" name="payment" id="pix" onChange={() => setOptionSelected("pix")} checked={optionSelected === 'pix'} />
              <div>
                <FaPix size={32} />
                <label htmlFor="pix">Pix</label>
              </div>
              <span>
                Realize o pagamento através do QR Code ou Código PIX após a finalização da compra.
                A aprovação é imediata!
              </span>
            </div>

            <div className={styles.wrapperPayment} style={{ flexDirection: "column" }}>
              <div className={styles.containerCreditCard}>
                <input type="radio" name="payment" id="creditCard" checked={optionSelected === 'credit_card' || optionSelected === 'new_credit_card'} onChange={() => setOptionSelected("credit_card")} />
                <div>
                  <FaCreditCard size={32} />
                  <label htmlFor="creditCard">Cartão de Crédito</label>
                </div>
                <span>
                  Confira os dados do cartão e parcele sua compra!
                </span>
              </div>
              {optionSelected === "new_credit_card" && (
                <>
                  <div className={styles.line}></div>
                  <div className={styles.newCardPanel}>
                    <div className={styles.newCardHeader}>
                      <h3 className={styles.newCardTitle}>Dados do cartão</h3>
                      <button
                        type="button"
                        className={styles.newCardClose}
                        aria-label="Fechar"
                        onClick={() => setOptionSelected("credit_card")}
                      >
                        ×
                      </button>
                    </div>
                    <div className={styles.newCardRow2}>
                      <div className={styles.inputCredit2}>
                        <label htmlFor="cardSurname">Apelido do cartão</label>
                        <input
                          id="cardSurname"
                          placeholder="Ex.: Cartão principal"
                          type="text"
                          value={cardSurname}
                          onChange={(e) => setCardSurname(e.target.value)}
                        />
                      </div>
                      <div className={styles.inputCredit2}>
                        <label htmlFor="cardName">Nome no cartão (titular)</label>
                        <input
                          id="cardName"
                          type="text"
                          placeholder="Como impresso no cartão"
                          value={cardName}
                          onChange={(e) => setCardName(e.target.value)}
                        />
                      </div>
                    </div>
                    <MpCardSecureFields onFieldValidity={onMpFieldValidity} />
                    <div className={styles.newCardRow2}>
                      <div className={styles.inputCredit2}>
                        <label htmlFor="idType">Documento do titular</label>
                        <select
                          id="idType"
                          value={identificationType}
                          onChange={(e) => setIdentificationType(e.target.value)}
                          className={styles.idSelect}
                        >
                          {identificationTypes.map((t) => (
                            <option key={t.id} value={t.id}>
                              {t.name}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div className={styles.inputCredit2}>
                        <label htmlFor="idNumber">Número do documento</label>
                        <input
                          id="idNumber"
                          type="text"
                          inputMode="numeric"
                          autoComplete="off"
                          placeholder="000.000.000-00"
                          value={identificationNumber}
                          onChange={(e) => setIdentificationNumber(e.target.value)}
                        />
                      </div>
                    </div>
                  </div>
                </>
              )}
              {optionSelected === 'credit_card' &&
                <>
                  <div className={styles.line}></div>
                  <div style={{ display: 'flex', flexDirection: 'column', width: '100%', alignItems: 'flex-start', padding: '0 32px' }}>
                    {cards.map((card) => (
                      <div key={card.id} className={styles.card} style={{ cursor: 'pointer', color: cardSelected === card.id ? '#D8372C' : undefined }} onClick={() => handleSelectCard(card.id)}>
                        <p>{card.name}</p>
                        <p>**** {card.lastFourDigits}</p>
                      </div>
                    ))}
                  </div>
                </>
              }
              <div style={{ display: 'flex', width: '100%', justifyContent: 'space-between', marginTop: 16 }}>
                {cardSelected && (
                  <div className={styles.savedCardCvv}>
                    <MpSavedCardSecurityCode
                      key={cardSelected}
                      onValidityChange={onSavedCardSecurityCodeValidity}
                    />
                  </div>
                )}

                {(optionSelected === 'credit_card' || optionSelected === 'new_credit_card') &&
                  <div className={styles.parcelas}>
                    <h4>Parcelas</h4>
                    <Select
                      selectedValue={parcelas}
                      setSelectedValue={setParcelas}
                      selectedValues={["1", "2", "3", "4", "5", "6", "7", "8", "9", "10", "11"]}
                    />
                  </div>}
              </div>
            </div>
            {optionSelected !== "new_credit_card" && (
              <button
                type="button"
                className={styles.button}
                disabled={isSavingCard}
                onClick={() => void handleNewCard()}
              >
                Adicionar novo cartão
              </button>
            )}
          </div>
          {error && <span style={{ color: '#D8372C' }}>{error}</span>}
          <button
            type="submit"
            className={styles.confirmButton}
            disabled={isCreatingSavedCardToken}
          >
            {isCreatingSavedCardToken ? "Carregando..." : "Confirmar compra"}
          </button>
        </form>}
      </div>
    </div>
  );
}
